// "Create New Shop" form (My Shop and Provider My Shop). Everything shown here
// -- remaining quota, price, allowed types -- comes from GET /shops/mine; the
// server re-checks role, quota, price and payment on submit, so this form can
// never create a shop the account is not allowed to have.
//
// Paid creation reuses Elakay's payment flow: the server charges the saved
// card; when the bank asks for 3-D Secure, Stripe's confirmCardPayment runs
// here and the server re-reads the PaymentIntent before activating the shop.
import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FaSpinner, FaStore } from "react-icons/fa";
import { elakayAutoText as text } from "../pageInside/dataElakay";
import { createAccountShop } from "../auth/apiShops";
import { afterShopCreated } from "./accountShopsStore";
import { completeShopPayment } from "./shopPaymentClient";
import { formatShopPrice, shopErrorMessage, shopTypeLabel } from "./shopLabels";
import AddressLocationSelector from "../pageInside/addressLocation/AddressLocationSelector";
import { toLocationValue, fromLocationValue } from "../pageInside/addressLocation/addressLocationData";
import "./shops.css";

const EMPTY_ADDRESS = { Address1: "", Address2: "", city: "", State: "", postalCode: "", country: "" };

const CreateShopModal = ({ show, onHide, data, defaultType = "seller", onCreated }) => {
  const types = data?.types || [];
  const addresses = data?.addresses || [];
  const quota = data?.quota || { count: 0, max: 0, canCreate: false };
  const pricing = data?.pricing || { amount: 0, currency: "usd", required: false };
  const [type, setType] = useState(types.includes(defaultType) ? defaultType : types[0] || defaultType);
  const [name, setName] = useState("");
  const [addressMode, setAddressMode] = useState(addresses.length ? "saved" : "new");
  const [addressId, setAddressId] = useState(addresses.find((a) => a.addressDefault)?._id || addresses[0]?._id || "");
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [confirmed, setConfirmed] = useState(false);
  // Creating a shop must not silently replace the shop currently managed by
  // My Shop.  In particular, switching to a brand-new (therefore empty) shop
  // made Admin, Customer and Orders all look as if legacy data had vanished.
  // The user can still opt in explicitly, or switch from the common ShopBar.
  const [activate, setActivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) return;
    setType(types.includes(defaultType) ? defaultType : types[0] || defaultType);
    setName("");
    setAddressMode(addresses.length ? "saved" : "new");
    setAddressId(addresses.find((a) => a.addressDefault)?._id || addresses[0]?._id || "");
    setAddress(EMPTY_ADDRESS);
    setConfirmed(false);
    setActivate(false);
    setError(null);
    // Reset only when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const nameError = name.trim() && (name.trim().length < 2 || name.trim().length > 80)
    ? text("h_msh_name_length", "The shop name must be 2 to 80 characters.")
    : null;
  const addressMissing = addressMode === "new"
    ? ["Address1", "city", "country"].filter((key) => !address[key].trim())
    : (addressId ? [] : ["address"]);
  const canSubmit = quota.canCreate && types.includes(type) && name.trim().length >= 2 && !nameError && !addressMissing.length && confirmed && !busy;
  const priceText = useMemo(() => formatShopPrice(pricing), [pricing]);

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        type,
        name: name.trim(),
        expectedAmount: pricing.amount || 0,
        ...(addressMode === "saved" ? { addressId } : { address }),
      };
      const result = await createAccountShop(payload);
      let shop = result.shop;
      if (result.payment?.requiresAction && result.payment.clientSecret) {
        const confirmedResult = await completeShopPayment(shop._id, result.payment);
        shop = confirmedResult.shop || shop;
      }
      await afterShopCreated(shop, { activate });
      onCreated?.(shop);
      onHide?.();
    } catch (err) {
      setError(shopErrorMessage(err));
      // Quota / price may have changed: show the server's current state.
      afterShopCreated(null).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const setAddressField = (key) => (event) => setAddress((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered size="lg" contentClassName="msh-modal" aria-labelledby="msh-create-title">
      <form onSubmit={submit} noValidate>
        <Modal.Header closeButton={!busy}>
          <Modal.Title id="msh-create-title" as="h2" className="msh-modal-title">
            <FaStore aria-hidden="true" /> {text("h_msh_create_new_shop", "Create New Shop")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="msh-summary" role="status">
            <span className="msh-quota">{text("h_msh_quota", "{value0} / {value1} Shops", { value0: quota.count, value1: quota.max })}</span>
            <span>{text("h_msh_creation_price", "Creation price: {value0}", { value0: priceText })}</span>
          </div>
          {!quota.canCreate && (
            <div className="msh-alert msh-alert-warning" role="alert">{text("h_msh_max_reached", "Maximum shops reached.")}</div>
          )}

          <fieldset className="msh-fieldset" disabled={!quota.canCreate || busy}>
            <legend>{text("h_msh_shop_information", "Shop Information")}</legend>
            <div className="msh-grid">
              <label className="msh-field">
                <span>{text("h_msh_shop_name", "Shop Name")} *</span>
                <input className="msh-input" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} aria-invalid={nameError ? "true" : "false"} required />
                {nameError && <small className="msh-error">{nameError}</small>}
              </label>
              <label className="msh-field">
                <span>{text("h_msh_shop_type", "Shop Type")}</span>
                <select className="msh-input" value={type} onChange={(event) => setType(event.target.value)} disabled={types.length < 2}>
                  {types.map((value) => <option key={value} value={value}>{shopTypeLabel(value)}</option>)}
                </select>
              </label>
            </div>
          </fieldset>

          <fieldset className="msh-fieldset" disabled={!quota.canCreate || busy}>
            <legend>{text("h_msh_shop_address", "Shop Address")}</legend>
            {addresses.length > 0 && (
              <div className="msh-radio-row" role="radiogroup" aria-label={text("h_msh_shop_address", "Shop Address")}>
                <label><input type="radio" name="msh-address-mode" checked={addressMode === "saved"} onChange={() => setAddressMode("saved")} /> {text("h_msh_use_saved_address", "Use one of my addresses")}</label>
                <label><input type="radio" name="msh-address-mode" checked={addressMode === "new"} onChange={() => setAddressMode("new")} /> {text("h_msh_new_address", "Add a new address")}</label>
              </div>
            )}
            {addressMode === "saved" && addresses.length > 0 ? (
              <label className="msh-field">
                <span>{text("h_msh_saved_addresses", "My addresses")}</span>
                <select className="msh-input" value={addressId} onChange={(event) => setAddressId(event.target.value)}>
                  {addresses.map((a) => (
                    <option key={a._id} value={a._id}>{[a.shopName, a.Address1, a.city, a.State, a.postalCode, a.country].filter(Boolean).join(", ")}</option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="msh-grid">
                <label className="msh-field msh-field-wide"><span>{text("h_msh_address_line1", "Address")} *</span><input className="msh-input" value={address.Address1} onChange={setAddressField("Address1")} autoComplete="address-line1" /></label>
                <label className="msh-field msh-field-wide"><span>{text("h_msh_address_line2", "Address line 2")}</span><input className="msh-input" value={address.Address2} onChange={setAddressField("Address2")} autoComplete="address-line2" /></label>
                {/* Added to the account's address book on creation, then
                    referenced by ID (Shop.addressId) -- never copied. */}
                <AddressLocationSelector
                  idPrefix="msh-new-address"
                  value={toLocationValue(address)}
                  onChange={(location) => setAddress((old) => fromLocationValue(old, location, { country: "country", state: "State", city: "city", postalCode: "postalCode" }))}
                  labels={{ country: `${text("h_msh_country", "Country")} *`, city: `${text("h_msh_city", "City")} *` }}
                  classNames={{ field: "msh-field", input: "msh-input", status: "msh-field-wide" }}
                />
              </div>
            )}
            <small className="msh-hint">{text("h_msh_address_by_reference", "The shop uses this address from your address book. Editing it in My Addresses updates every shop that uses it.")}</small>
          </fieldset>

          <div className="msh-confirm">
            <label>
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={!quota.canCreate || busy} />
              {pricing.required
                ? text("h_msh_confirm_paid", "I confirm the creation of this shop and the payment of {value0} with my saved card.", { value0: priceText })
                : text("h_msh_confirm_free", "I confirm the creation of this shop.")}
            </label>
            <label>
              <input type="checkbox" checked={activate} onChange={(event) => setActivate(event.target.checked)} disabled={busy} />
              {text("h_msh_switch_after", "Manage this new shop right after its creation")}
            </label>
          </div>
          {error && <div className="msh-alert msh-alert-error" role="alert">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="msh-btn" onClick={onHide} disabled={busy}>{text("h_msh_cancel", "Cancel")}</button>
          <button type="submit" className="msh-btn msh-btn-primary" disabled={!canSubmit}>
            {busy ? <FaSpinner className="msh-spin" aria-hidden="true" /> : <FaStore aria-hidden="true" />}
            {pricing.required
              ? text("h_msh_create_and_pay", "Create and pay {value0}", { value0: priceText })
              : text("h_msh_create_new_shop", "Create New Shop")}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default CreateShopModal;
